import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogModule as MatDialogModule,
} from '@angular/material/legacy-dialog';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import {
  MatLegacySnackBarModule as MatSnackBarModule,
  MatLegacySnackBar as MatSnackBar,
} from '@angular/material/legacy-snack-bar';
import { AgGridModule } from 'ag-grid-angular';
import { GridApi, GridOptions, IServerSideDatasource } from 'ag-grid-community';

import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { AssemblyOrderApiService } from 'v2/modules/shared/services/api/assembly-order-api.service';
import { PurchaseOrderApiService } from 'v2/modules/shared/services/api/purchase-order-api.service';
import { SalesOrderApiService } from 'v2/modules/shared/services/api/sales-order-api.service';

import { SalesOrderLineItemsAssemblyOrderDialogComponent } from '../sales-order-line-items-assembly-order-dialog/sales-order-line-items-assembly-order-dialog.component';
import { SalesOrderLineItemsTransferOrderDialogComponent } from '../sales-order-line-items-transfer-order-dialog/sales-order-line-items-transfer-order-dialog.component';

import { DialogConfig } from './../../../shared/configs/dialog.config';

@Component({
  selector: 'lum-sales-order-ag-table-line-items',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatMenuModule,
    MatSnackBarModule,
    MatDialogModule,
    SalesOrderLineItemsTransferOrderDialogComponent,
    SalesOrderLineItemsAssemblyOrderDialogComponent,
  ],
  templateUrl: './sales-order-ag-table-line-items.component.html',
  styleUrls: ['./sales-order-ag-table-line-items.component.scss'],
})
export class SalesOrderAgTableLineItemsComponent {
  gridOptions :GridOptions;
  private gridApi! :GridApi;

  constructor(
    private salesOrderApiservice :SalesOrderApiService,
    private assemblyOrderApiService :AssemblyOrderApiService,
    private purchaseOrderApiService :PurchaseOrderApiService,
    private snackBar :MatSnackBar,
    private matDialog :MatDialog,
  ) {
    this.gridOptions = {
      rowModelType: 'serverSide', // Using server-side row model
      rowSelection: 'multiple',
      paginationPageSize: 100,
      columnDefs: [
        {
          headerCheckboxSelection: true,
          checkboxSelection: true,
          filter: false,
          filterParams: null,
          floatingFilter: false,
          lockPinned: true,
          lockPosition: true,
          resizable: false,
          sortable: false,
          suppressColumnsToolPanel: true,
          suppressMenu: true,
          suppressMovable: true,
          suppressNavigable: true,
          width: 40,
          maxWidth: 40,
          minWidth: 40,
          pinned: 'left',
        },
        {
          headerName: 'Order #',
          field: 'salesOrder.orderNumber',
          sortable: true,
          filter: true,
          resizable: true,
          width: 150,
        },
        {
          headerName: 'Ordered SKU',
          field: 'sku',
          sortable: true,
          filter: true,
          resizable: true,
          width: 150,
        },
        {
          headerName: 'Product Name',
          field: 'name',
          sortable: true,
          filter: true,
          resizable: true,
          width: 200,
        },
        {
          headerName: 'Ordered Qty',
          field: 'orderedQuantity',
          sortable: true,
          filter: 'agNumberColumnFilter', // Number filter
          resizable: true,
          width: 150,
        },
        {
          headerName: 'UOM',
          field: 'orderedUom.name',
          sortable: true,
          filter: true,
          resizable: true,
          width: 100,
        },
        {
          headerName: 'Avail. Stock',
          field: 'numberOfStocks',
          sortable: true,
          filter: true,
          resizable: true,
          width: 150,
        },
        {
          headerName: 'Tags',
          field: 'rfq.rfqTags',
          sortable: true,
          filter: true,
          resizable: true,
          width: 150,
          cellRenderer: (params) => {
            if (Array.isArray(params.value) && params.value.length > 0) {
              return params.value
                .map((data :any) => {
                  return `<span class="lum-chip mr-2">${data?.tag?.name || 'No Name'}</span>`;
                })
                .join('');
            }

            return '';
          },
        },
        {
          headerName: 'Preferred Supplier',
          field: 'rfq.preferredVendor.factoryName',
          sortable: true,
          filter: true,
          resizable: true,
          width: 200,
        },
      ],
    };
  }

  onCreatePurchaseOrder() :void {
    this.snackBar.open('Creating purchase order...', 'Dismiss');
    this.purchaseOrderApiService
      .createByOrderLineItems(this.selectedIds)
      .subscribe({
        next: (response :any) => {
          this.snackBar.open('Purchase order created successfully', 'Dismiss', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (err) => {
          console.error('Error creating purchase order:', err);
          this.snackBar.open('Error creating purchase order', 'Dismiss', {
            duration: SnackBarConfig.ERROR.duration,
          });
        },
      });
  }

  onToggleTransferOrderDialog() :void {
    this.matDialog.open(SalesOrderLineItemsTransferOrderDialogComponent, {
      width: DialogConfig.Size.SMALL,
      data: {
        orderItemIds: this.selectedIds,
      },
    });
  }

  onToggleAssemblyOrderDialog() :void {
    this.matDialog.open(SalesOrderLineItemsAssemblyOrderDialogComponent, {
      width: DialogConfig.Size['X_SMALL'],
      data: {
        orderItemIds: this.selectedIds,
      },
    });
  }

  onGridReady(params :any) :void {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();

    // Set up mock server-side data source
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Creates a mock server-side data source
   */
  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params) => {
        // Server-side request params
        this.salesOrderApiservice
          .getLineItems({
            relations: [
              'order',
              'orderedUom',
              'rfq.tags.tag',
              'rfq.preferredVendor',
            ].join('|'),
            sortDirection: 'desc',
            sortField: 'created_at',
          })
          .subscribe({
            next: (response :any) => {
              params.successCallback(response.data, response.data.length); // Return data and total count
            },
            error: (err) => {
              console.error('Error fetching line items:', err);
              params.failCallback();
            },
          });

        // console.log('Server-side request params:', params);

        // // Mock data for demonstration
        // const mockData = Array.from({ length: 1000 }, (_, index) => ({
        //   id: index + 1,
        //   internal_sku: `Item ${index + 1}`,
        //   product_name: `Product ${index + 1}`,
        //   estimated_arrival_date: `2023-${(index % 12) + 1}-01`,
        //   location: `Location ${index + 1}`,
        //   lot: `Lot ${index + 1}`,
        //   ordered_qty: Math.floor(Math.random() * 1000),
        //   uom: 'Unit',
        //   available_qty: Math.floor(Math.random() * 500),
        // }));

        // // Simulate server-side delay
        // setTimeout(() => {
        //   const { startRow, endRow } = params.request;
        //   const rows = mockData.slice(startRow, endRow); // Paginate mock data
        //   params.successCallback(rows, mockData.length); // Return data and total count
        // }, 500);
      },
    };
  }

  get selectedIds() :number[] {
    const selectedIds :number[] = [];

    this.gridApi.forEachNode((node) => {
      if (node.isSelected() && node.data?.id) {
        selectedIds.push(node.data.id);
      }
    });

    return selectedIds;
  }
}
